<template>
  <div class="sales-team-members">
    <legend>
      <strong>{{ $t('members') }}</strong>
    </legend>

    <!-- Add Member Section -->
    <b-form-group
      horizontal
      :label="$t('addMember')"
      label-for="userSelect"
      label-class="text-md-right"
      :label-cols="4"
      breakpoint="md"
      class="mb-3"
    >
      <div class="d-flex flex-column">
        <div class="d-flex mb-2">
          <multiselect
            id="userSelect"
            class="multiselect-users flex-grow-1"
            v-model="selectedUser"
            :options="availableUsers"
            track-by="number"
            v-on:search-change="onFilterUsers"
            :filter="true"
            :internal-search="false"
            :close-on-select="true"
            :clear-on-select="true"
            :multiple="false"
            :show-labels="false"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :placeholder="'Benutzer ' + $t('select') + '/suchen'"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }} - {{ option.email }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.firstname }} {{ option.lastname }} - {{ option.email }}
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
        </div>
        <div class="d-flex">
          <multiselect
            id="parentSelect"
            class="multiselect-parent flex-grow-1"
            v-model="selectedParent"
            :options="parentOptions"
            track-by="_id"
            label="displayName"
            :filter="false"
            :close-on-select="true"
            :clear-on-select="true"
            :multiple="false"
            :show-labels="false"
            :allow-empty="true"
            :deselectLabel="$t('deselectLabel')"
            selectLabel=""
            selectedLabel=""
            :placeholder="$t('selectParent')"
          >
            <template slot="singleLabel" slot-scope="{ option }">
              {{ option.displayName }}
            </template>
            <template slot="option" slot-scope="{ option }">
              {{ option.displayName }}
            </template>
            <template slot="noResult">
              <div>{{ $t('no_results') }}</div>
            </template>
          </multiselect>
          <button
            class="btn btn-success ml-2"
            type="button"
            style="white-space: nowrap; font-size: 0.875rem;"
            :disabled="!selectedUser || isLoading"
            @click="onAddMember"
          >
            <i class="fa fa-plus mr-1"></i>
            {{ $t('add') }}
          </button>
        </div>
      </div>
    </b-form-group>

    <div v-if="isLoading" class="text-center p-4">
      {{ $t('loading') }}
    </div>

    <div v-else-if="members.length === 0" class="text-muted p-4">
      {{ $t('no_members') }}
    </div>

    <!-- Grouped by Root Members -->
    <div v-else class="member-tree">
      <div v-for="rootMember in rootMembers" :key="rootMember._id" class="root-member-group mb-3">
        <div class="group-tab">
          <i class="fa fa-sitemap mr-2"></i>
          <span>{{ getMemberName(rootMember) }}</span>
        </div>
        <div class="member-list">
          <div
            v-for="member in getSubtree(rootMember._id)"
            :key="member._id"
            class="member-item p-d-flex p-jc-between p-ai-center"
            :style="{ paddingLeft: member.depth * 30 + 5 + 'px' }"
            :class="'depth-' + member.depth"
          >
            <div class="p-flex-grow-1 p-d-flex">
              <div class="depth-indicator-wrapper">
                <span class="depth-indicator" v-if="member.depth > 0">
                  <i class="fa fa-level-up fa-rotate-90 text-muted"></i>
                </span>
              </div>
              <div class="member-content">
                <div>
                  <span class="level-badge">{{ member.depth + 1 }}</span>
                  <strong>
                    {{ getMemberName(member) }}
                  </strong>
                  <small class="text-muted"> - {{ member.user ? member.user.email : '' }} </small>
                </div>
                <small class="text-muted d-block">
                  {{ $t('commission') }}: {{ getCommissionRate(member) }}%
                </small>
                <small class="text-muted d-block">
                  {{ $t('since') }} {{ $dayjs(member.createdAt).format('DD.MM.YYYY HH:mm') }}
                </small>
              </div>
            </div>
            <div class="p-d-flex p-ai-center member-actions">
              <Dropdown
                :value="member.parent ? member.parent._id : null"
                :options="getParentOptionsForMember(member)"
                optionLabel="displayName"
                optionValue="_id"
                :placeholder="$t('move')"
                class="member-parent-select mr-2"
                @change="onParentChange(member, $event)"
                :disabled="isLoading"
              />
              <Button
                v-if="!hasChildren(member)"
                icon="pi pi-trash"
                class="p-button-danger p-button-sm"
                @click="removeMember(member)"
                :disabled="isLoading"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';

export default {
  name: 'SalesTeamMembers',
  components: {
    Multiselect,
    Button,
    Dropdown,
  },
  props: {
    teamId: {
      type: String,
      required: true,
    },
    team: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      selectedUser: null,
      selectedParent: null,
      filteredUsers: [],
    };
  },
  computed: {
    ...mapGetters(['getSalesMembers', 'getUsers']),
    members() {
      return this.getSalesMembers || [];
    },
    // Filter out users that are already members
    availableUsers() {
      const memberUserIds = this.members.map((m) => m.user?._id?.toString()).filter(Boolean);
      return this.filteredUsers.filter((user) => !memberUserIds.includes(user._id?.toString()));
    },
    // Get root members (members with depth 0 or no parent)
    rootMembers() {
      return this.members.filter((m) => !m.parent || m.depth === 0);
    },
    // Format members for parent selection dropdown
    parentOptions() {
      const options = [
        {
          _id: null,
          displayName: this.$t('root'),
        },
      ];
      return options.concat(
        this.members.map((member) => {
          const userName = member.user
            ? `${member.user.firstname} ${member.user.lastname}`
            : member.user?._id || 'Unknown';
          return {
            _id: member._id,
            displayName: `${userName} (${this.$t('depth')}: ${member.depth})`,
          };
        })
      );
    },
  },
  methods: {
    ...mapActions([
      'fetchSalesMembersPaginated',
      'addSalesMember',
      'removeSalesMember',
      'moveSalesMember',
      'fetchUsersPaginated',
    ]),
    async loadMembers() {
      if (!this.teamId) {
        return;
      }

      this.isLoading = true;
      try {
        await this.fetchSalesMembersPaginated({
          pagination: {
            skip: 0,
            pageSize: 100, // Load up to 100 members for now
          },
          sorting: {
            sortField: 'depth',
            sortOrder: 1, // Sort by depth ascending
          },
          filters: {
            teamId: {
              value: this.teamId,
            },
          },
        });
      } catch (err) {
        console.error('Error loading members:', err);
      } finally {
        this.isLoading = false;
      }
    },
    async onFilterUsers(query) {
      // Fetch users with SALES department filter and optional search query
      await this.fetchUsersPaginated({
        pagination: {
          skip: 0,
          pageSize: 100, // Load up to 100 users
        },
        sorting: {
          sortField: 'lastname',
          sortOrder: 1,
        },
        filters: {
          departments: [{ value: 'SALES' }],
          ...(query && query.length > 0 ? { userSearch: { value: query } } : {}),
        },
      });
      this.filteredUsers = this.getUsers || [];
    },
    async onAddMember() {
      if (!this.selectedUser || !this.teamId) {
        return;
      }

      try {
        await this.addSalesMember({
          teamId: this.teamId,
          userId: this.selectedUser.id,
          parentId: this.selectedParent?._id || null,
        });
        // Clear selection and reload members
        this.selectedUser = null;
        this.selectedParent = null;
        await this.loadMembers();
      } catch (err) {
        console.error('Error adding member:', err);
      }
    },
    getMemberName(member) {
      if (!member) return '';
      if (member.user) {
        return `${member.user.firstname} ${member.user.lastname}`;
      }
      return member.user?._id || 'Unknown';
    },
    getParentName(member) {
      if (!member || !member.parent) {
        return '';
      }
      if (member.parent.user) {
        return `${member.parent.user.firstname} ${member.parent.user.lastname}`;
      }
      return member.parent._id || '';
    },
    getSubtree(rootId) {
      // Get all members in this subtree using depth-first traversal
      const subtree = [];

      const addMemberAndChildren = (memberId) => {
        const member = this.members.find((m) => m._id && m._id.toString() === memberId.toString());
        if (!member) return;

        // Add current member
        subtree.push(member);

        // Get direct children sorted by createdAt
        const children = this.members
          .filter((m) => m.parent && m.parent._id.toString() === memberId.toString())
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        // Recursively add each child and their subtree
        children.forEach((child) => addMemberAndChildren(child._id));
      };

      addMemberAndChildren(rootId);
      return subtree;
    },
    getDescendantCount(member) {
      if (!member || !member._id) return 0;
      const subtree = this.getSubtree(member._id);
      return subtree.length - 1; // Exclude the member itself
    },
    getChildrenCount(member) {
      if (!member || !member._id) return 0;
      return this.members.filter(
        (m) => m.parent && m.parent._id.toString() === member._id.toString()
      ).length;
    },
    getCommissionRate(member) {
      if (!member || !this.team || !this.team.commissionByDepth) return 0;
      const rate = this.team.commissionByDepth[member.depth];
      if (rate === undefined || rate === null) return 0;
      // Convert from decimal to percentage (0.05 -> 5)
      return (rate * 100).toFixed(2);
    },
    getParentOptionsForMember(member) {
      if (!member || !member._id) return [];

      // Get all descendants of this member (cannot move under own descendant)
      const descendants = this.getSubtree(member._id).map((m) => m._id.toString());

      // Build options: root option + all members except this member and its descendants
      const options = [
        {
          _id: null,
          displayName: this.$t('root'),
        },
      ];

      const validParents = this.members
        .filter((m) => {
          // Exclude the member itself and its descendants
          return (
            m._id.toString() !== member._id.toString() && !descendants.includes(m._id.toString())
          );
        })
        .map((m) => {
          const userName = m.user
            ? `${m.user.firstname} ${m.user.lastname}`
            : m.user?._id || 'Unknown';
          return {
            _id: m._id,
            displayName: `${userName} (${this.$t('level')} ${m.depth + 1})`,
          };
        });

      return options.concat(validParents);
    },
    async onParentChange(member, event) {
      if (!member || !member._id) return;

      const newParentId = event.value;

      try {
        await this.moveSalesMember({ memberId: member._id, newParentId });
        // Reload members after move
        await this.loadMembers();
      } catch (err) {
        console.error('Error moving member:', err);
        // Reload to reset the dropdown to original value
        await this.loadMembers();
      }
    },
    hasChildren(member) {
      if (!member || !member._id) {
        return false;
      }
      // Check if any member has this member as parent
      return this.members.some(
        (m) => m.parent && m.parent._id.toString() === member._id.toString()
      );
    },
    async removeMember(member) {
      if (!member || !member._id) {
        return;
      }

      try {
        await this.removeSalesMember({ memberId: member._id, mode: 'CASCADE' });
        // Reload members after removal
        await this.loadMembers();
      } catch (err) {
        console.error('Error removing member:', err);
      }
    },
  },
  watch: {
    teamId: {
      handler() {
        this.loadMembers();
      },
      immediate: true,
    },
  },
  async mounted() {
    // Fetch only SALES department users
    await this.fetchUsersPaginated({
      pagination: {
        skip: 0,
        pageSize: 200,
      },
      sorting: {
        sortField: 'lastname',
        sortOrder: 1,
      },
      filters: {
        departments: [{ value: 'SALES' }],
      },
    });
    this.filteredUsers = this.getUsers || [];
    this.loadMembers();
  },
};
</script>

<style scoped lang="scss">
.sales-team-members {
  legend {
    position: relative;
  }

  .member-tree {
    .root-member-group {
      margin-bottom: 1rem;

      .group-tab {
        display: inline-block;
        padding: 0.4rem 0.75rem;
        background: $gray-200;
        border: 1px solid $gray-200;
        border-bottom: none;
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: $gray-700;
        margin-bottom: -1px;
        position: relative;
        z-index: 1;
      }

      .member-list {
        border-radius: 0.25rem;
        border: 1px solid $gray-200;
      }

      .member-item {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid $gray-200;
        transition: background-color 0.15s ease;
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &:first-child {
          border-top-left-radius: 0.25rem;
          border-top-right-radius: 0.25rem;
        }

        &:last-child {
          border-bottom-left-radius: 0.25rem;
          border-bottom-right-radius: 0.25rem;
          border-bottom: none;
        }

        &:hover {
          background-color: $white;
        }

        // Depth-based background shading
        &.depth-0 {
          background-color: $white;
          font-weight: 500;
        }

        &.depth-1 {
          background-color: lighten($white, 0%);
        }

        &.depth-2 {
          background-color: lighten($white, 0%);
        }

        .depth-indicator-wrapper {
          width: 20px;
          flex-shrink: 0;
          display: flex;
          align-items: flex-start;
          padding-top: 2px;
        }

        .depth-indicator {
          opacity: 0.5;
        }

        .member-content {
          flex-grow: 1;
          min-width: 0; // Prevent text overflow issues
        }

        .p-flex-grow-1 {
          min-width: 0;
          flex-grow: 1;
        }

        .p-d-flex {
          display: flex;
        }

        .p-ai-center {
          align-items: center;
        }

        .level-badge {
          display: inline-block;
          padding: 0.15rem 0.45rem 0.05rem 0.4rem;
          margin-right: 0;
          font-size: 0.75rem;
          font-weight: 600;
          background: $green;
          color: $gray-100;
          border-radius: 0.2rem;
        }

        .member-actions {
          flex-shrink: 0;
        }

        .member-parent-select {
          width: 200px;
          font-size: 0.875rem;
        }
      }
    }
  }
}
</style>
